import { Color } from './java/Color';
import {
  Graphics2D, Font, FontMetrics, BasicStroke, GradientPaint, RenderingHints,
} from './java/Graphics2D';
import { Random } from './java/Random';
import { Game } from './Game';
import { GameObject } from './GameObject';
import { Player } from './Player';
import { Perks } from './Perks';
import { Achievements } from './Achievements';
import { GamePalette } from './GamePalette';

export class HUD {

  bounds = 0;
  static HEALTH = 100;

  private score = 0;
  private level = 1;
  private points = 0;

  // Run stats
  private ticksSurvived = 0;
  private healthUpgrades = 0;
  private speedUpgrades = 0;
  private refills = 0;
  private kills = 0;

  static readonly MAX_TIER = 10;

  // Health bar crack/shatter
  private frameTick = 0;
  private static readonly crackRng = new Random(42); // fixed seed for consistent cracks

  private static readonly FONT_SCORE = new Font('Arial', Font.BOLD, 32);
  private static readonly FONT_LABEL = new Font('Arial', Font.BOLD, 13);
  private static readonly FONT_STAT = new Font('Arial', Font.PLAIN, 16);
  private static readonly FONT_LEVEL_BG = new Font('Arial', Font.BOLD, 600);
  private static readonly FONT_TIER = new Font('Arial', Font.BOLD, 10);
  private static readonly FONT_LEVEL_UP = new Font('Arial', Font.BOLD, 28);

  // Colors
  private static readonly BAR_BG = new Color(22, 30, 44);
  private static readonly BAR_BORDER = new Color(40, 52, 70);
  private static readonly TEXT = new Color(230, 234, 240);
  private static readonly TEXT_DIM = new Color(100, 112, 128);
  // ACCENT is now dynamic — use GamePalette.accent() for in-game elements
  private static readonly HEALTH_HIGH = new Color(72, 199, 142);
  private static readonly HEALTH_MID = new Color(245, 195, 68);
  private static readonly HEALTH_LOW = new Color(235, 87, 87);
  private static readonly TIER_HEALTH = new Color(72, 199, 142);
  private static readonly TIER_SPEED = new Color(100, 180, 220);
  private static readonly TIER_REFILL = new Color(245, 195, 68);
  private static readonly TIER_BG = new Color(30, 38, 52);
  // Top readability gradient — geometry is fixed, so build the paint once
  private static readonly TOP_GRADIENT = new GradientPaint(
    0, 0, new Color(10, 12, 18, 160), 0, 90, new Color(10, 12, 18, 0));
  private static readonly FONT_TOAST_ICON = new Font('Arial', Font.BOLD, 18);

  // Level-up announcement
  private levelUpBanner = 0;
  private announceTimer = 0;
  private announceText = '';
  private announceSubtext = '';
  private isWaveAnnounce = false;
  private waveCount = 1;
  private lastBossLevel = 0; // tracks last level a boss was beaten
  private static readonly FONT_ANNOUNCE = new Font('Arial', Font.BOLD, 64);
  private static readonly FONT_ANNOUNCE_SUB = new Font('Arial', Font.BOLD, 20);

  // Score milestone pulse
  private static readonly MILESTONE_INTERVAL = 1000;
  private scorePulse = 0;
  private lastMilestone = 0;

  // Achievement toast queue
  private readonly toastQueue: string[] = [];
  private currentToast: string | null = null;
  private toastTimer = 0;
  private toastSlide = 0; // 0=hidden, 1=visible
  private static readonly TOAST_DURATION = 180; // 3 seconds
  private static readonly FONT_SCORE_BIG = new Font('Arial', Font.BOLD, 42);
  private static readonly GOLD = new Color(255, 210, 80);

  tick(): void {
    const maxHealth = 100 + Math.trunc(this.bounds / 2);
    HUD.HEALTH = Game.clamp(HUD.HEALTH, 0, maxHealth);

    // Score multiplied by streak + perk bonus
    const mult = Math.trunc(this.getPlayerMultiplier() * Perks.getScoreMultiplier());
    this.score += mult;
    this.points += mult;
    this.ticksSurvived++;

    this.frameTick++;
    if (this.levelUpBanner > 0.01) this.levelUpBanner *= 0.96;
    else this.levelUpBanner = 0;
    if (this.announceTimer > 0) this.announceTimer--;

    // Score milestone check
    const currentMilestone = Math.trunc(this.score / HUD.MILESTONE_INTERVAL);
    if (currentMilestone > this.lastMilestone && this.lastMilestone >= 0) {
      this.scorePulse = 1;
    }
    this.lastMilestone = currentMilestone;
    if (this.scorePulse > 0.01) this.scorePulse *= 0.92;
    else this.scorePulse = 0;

    // Achievement toast tick
    if (this.currentToast !== null) {
      this.toastTimer--;
      const target = this.toastTimer > 30 ? 1 : 0;
      this.toastSlide += (target - this.toastSlide) * 0.12;
      if (this.toastTimer <= 0) {
        this.currentToast = null;
        this.toastSlide = 0;
      }
    } else if (this.toastQueue.length > 0) {
      this.currentToast = this.toastQueue.shift()!;
      this.toastTimer = HUD.TOAST_DURATION;
      this.toastSlide = 0;
    }
    // Check for new achievements
    if (Achievements.hasNew()) {
      for (const a of Achievements.popNewlyUnlocked()) {
        this.toastQueue.push(a.name);
      }
    }
  }

  triggerLevelUpBanner(): void {
    this.levelUpBanner = 1;
    // Level announcements are now just the watermark flash — no center text
    this.isWaveAnnounce = false;
    this.announceTimer = 0;
  }

  triggerWaveAnnounce(): void {
    this.waveCount++;
    this.lastBossLevel = this.level;
    this.isWaveAnnounce = true;
    this.announceText = 'WAVE ' + this.waveCount;
    this.announceSubtext = this.waveCount === 1 ? 'Good luck' : 'Boss defeated - next wave';
    this.announceTimer = 110;
    this.levelUpBanner = 1;
  }

  getLevelInWave(): number {
    return this.level - this.lastBossLevel;
  }

  render(g: Graphics2D): void {
    const g2: Graphics2D = g;
    g2.setRenderingHint(RenderingHints.KEY_TEXT_ANTIALIASING, RenderingHints.VALUE_TEXT_ANTIALIAS_ON);
    g2.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);

    // Subtle top gradient overlay for readability
    g2.setPaint(HUD.TOP_GRADIENT);
    g2.fillRect(0, 0, Game.WIDTH, 90);

    // Health bar
    const maxHealth = 100 + Math.trunc(this.bounds / 2);
    const healthPct = HUD.HEALTH / maxHealth;
    const barW = 200 + this.bounds;
    const barH = 14;
    const barX = 24;
    const barY = 20;

    // Border tremble below 25%
    let bx = barX, by = barY;
    if (healthPct < 0.25 && healthPct > 0) {
      const trembleIntensity = (0.25 - healthPct) / 0.25;
      bx += Math.trunc((Math.sin(this.frameTick * 1.2) * 1.5) * trembleIntensity);
      by += Math.trunc((Math.cos(this.frameTick * 1.7) * 0.8) * trembleIntensity);
    }

    // Background
    g2.setColor(HUD.BAR_BG);
    g2.fillRoundRect(bx, by, barW, barH, 7, 7);

    // Fill
    const healthColor = this.getHealthColor(healthPct);
    const fillW = Math.trunc(barW * healthPct);

    if (fillW > 0 && healthPct >= 0.25) {
      // Normal or cracked fill
      g2.setColor(healthColor);
      g2.fillRoundRect(bx, by, fillW, barH, 7, 7);
    } else if (fillW > 0) {
      // Fragmented fill below 25% — draw in segments with gaps
      const flickerIntensity = (0.25 - healthPct) / 0.25;
      const segCount = 5 + Math.trunc(flickerIntensity * 4);
      const segW = Math.max(Math.trunc(fillW / segCount), 2);
      for (let i = 0; i < segCount; i++) {
        const sx = bx + i * Math.trunc(fillW / segCount);
        let sw = segW - 1; // 1px gap between segments
        if (sx + sw > bx + fillW) sw = bx + fillW - sx;
        if (sw <= 0) continue;

        // Flicker — some segments randomly dim
        const flicker = flickerIntensity > 0.5 && ((this.frameTick + i * 7) % 5 === 0);
        const segColor = flicker
          ? new Color(Math.trunc(healthColor.getRed() / 2), Math.trunc(healthColor.getGreen() / 2), Math.trunc(healthColor.getBlue() / 2))
          : healthColor;

        // Vertical offset jitter on fragments
        const jitter = Math.trunc(Math.sin(this.frameTick * 0.8 + i * 2.3) * 1.5 * flickerIntensity);
        g2.setColor(segColor);
        g2.fillRect(sx, by + jitter, sw, barH);
      }
    }

    // Crack lines below 50%
    if (healthPct < 0.5 && healthPct > 0 && fillW > 4) {
      const crackIntensity = (0.5 - healthPct) / 0.5; // 0 at 50%, 1 at 0%
      const crackCount = 2 + Math.trunc(crackIntensity * 5);
      HUD.crackRng.setSeed(42); // reset seed for consistent positions each frame
      g2.setColor(new Color(0, 0, 0, 60 + Math.trunc(crackIntensity * 120)));
      for (let i = 0; i < crackCount; i++) {
        const cx = bx + 4 + HUD.crackRng.nextInt(Math.max(fillW - 8, 1));
        const cy1 = by + HUD.crackRng.nextInt(4);
        const cy2 = by + barH - HUD.crackRng.nextInt(4);
        const midX = cx + HUD.crackRng.nextInt(5) - 2;
        const midY = by + Math.trunc(barH / 2) + HUD.crackRng.nextInt(4) - 2;
        g2.drawLine(cx, cy1, midX, midY);
        g2.drawLine(midX, midY, cx + HUD.crackRng.nextInt(3) - 1, cy2);
      }
    }

    // Border
    g2.setColor(healthPct < 0.25
      ? this.lerpColor(HUD.BAR_BORDER, HUD.HEALTH_LOW, (0.25 - healthPct) / 0.25 * 0.5)
      : HUD.BAR_BORDER);
    g2.drawRoundRect(bx, by, barW, barH, 7, 7);

    // HP text
    g2.setFont(HUD.FONT_LABEL);
    g2.setColor(new Color(255, 255, 255, 180));
    const hpText = Math.trunc(HUD.HEALTH) + ' / ' + Math.trunc(maxHealth);
    let fm: FontMetrics = g2.getFontMetrics();
    g2.drawString(hpText, bx + Math.trunc((barW - fm.stringWidth(hpText)) / 2), by + 11);

    // Stats below health bar
    g2.setFont(HUD.FONT_STAT);
    g2.setColor(HUD.TEXT_DIM);
    const waveStr = 'W' + this.waveCount + ' LVL ' + this.getLevelInWave();
    g2.drawString(waveStr, barX, barY + 34);
    const waveWidth = g2.getFontMetrics().stringWidth(waveStr);
    const ptsStr = 'PTS ' + this.points;
    g2.drawString(ptsStr, barX + waveWidth + 10, barY + 34);
    if (Game.combatMode) {
      const ptsWidth = g2.getFontMetrics().stringWidth(ptsStr);
      g2.setColor(GamePalette.accent());
      g2.drawString('KILLS ' + Game.getRunKills(), barX + waveWidth + 10 + ptsWidth + 10, barY + 34);
    }

    // Ability indicators below stats
    this.renderAbilityBar(g2, barX, barY + 46);

    // Upgrade tiers — right side of top bar
    const tierX = Game.WIDTH - 200;
    const tierY = 16;
    this.drawTierBar(g2, tierX, tierY, 'HP', this.healthUpgrades, HUD.TIER_HEALTH);
    this.drawTierBar(g2, tierX, tierY + 18, 'SPD', this.speedUpgrades, HUD.TIER_SPEED);
    this.drawTierBar(g2, tierX, tierY + 36, 'REF', this.refills, HUD.TIER_REFILL);

    // Score — top center with milestone pulse
    const scoreCX = Math.trunc(Game.WIDTH / 2);
    const scoreY = 46;

    // Radial pulse ring on milestone
    if (this.scorePulse > 0.05) {
      const ringRadius = Math.trunc((1 - this.scorePulse) * 120);
      const ringAlpha = Math.trunc(this.scorePulse * 120);
      g2.setColor(new Color(HUD.GOLD.getRed(), HUD.GOLD.getGreen(), HUD.GOLD.getBlue(), Math.min(ringAlpha, 255)));
      g2.drawOval(scoreCX - ringRadius, scoreY - 15 - ringRadius, ringRadius * 2, ringRadius * 2);
      // Second ring, slightly delayed
      const ring2 = Math.trunc((1 - this.scorePulse * 0.8) * 80);
      const ring2Alpha = Math.trunc(this.scorePulse * 60);
      g2.setColor(new Color(HUD.GOLD.getRed(), HUD.GOLD.getGreen(), HUD.GOLD.getBlue(), Math.min(ring2Alpha, 255)));
      g2.drawOval(scoreCX - ring2, scoreY - 15 - ring2, ring2 * 2, ring2 * 2);
    }

    // Score number — bumps size and flashes gold on milestone
    const scoreFont = this.scorePulse > 0.1 ? HUD.FONT_SCORE_BIG : HUD.FONT_SCORE;
    const scoreColor = this.scorePulse > 0.1
      ? this.lerpColor(HUD.TEXT, HUD.GOLD, this.scorePulse)
      : HUD.TEXT;
    g2.setFont(scoreFont);
    g2.setColor(scoreColor);
    const scoreStr = String(this.score);
    fm = g2.getFontMetrics();
    g2.drawString(scoreStr, Math.trunc((Game.WIDTH - fm.stringWidth(scoreStr)) / 2), scoreY);

    // Score label
    g2.setFont(HUD.FONT_LABEL);
    const labelColor = this.scorePulse > 0.1
      ? this.lerpColor(HUD.TEXT_DIM, HUD.GOLD, this.scorePulse * 0.5)
      : HUD.TEXT_DIM;
    g2.setColor(labelColor);
    const scoreLabel = 'SCORE';
    fm = g2.getFontMetrics();
    g2.drawString(scoreLabel, Math.trunc((Game.WIDTH - fm.stringWidth(scoreLabel)) / 2), 18);

    // Large level watermark in background — flashes on level-up
    g2.setFont(HUD.FONT_LEVEL_BG);
    const watermarkAlpha = 20 + Math.trunc(this.levelUpBanner * 60);
    g2.setColor(GamePalette.accent(Math.min(watermarkAlpha, 255)));
    const displayLevel = this.getLevelInWave();
    const levelStr = displayLevel <= 9 ? '0' + displayLevel : '' + displayLevel;
    fm = g2.getFontMetrics();
    g2.drawString(levelStr, Math.trunc((Game.WIDTH - fm.stringWidth(levelStr)) / 2), Game.HEIGHT - 80);

    // Wave announcement — slam in, hold, fade out (only for wave changes)
    if (this.isWaveAnnounce && this.announceTimer > 0 && this.announceText.length > 0) {
      const t = 1 - (this.announceTimer / 110);

      let alpha: number, scaleF: number, offsetY: number;
      if (t < 0.12) {
        const p = t / 0.12;
        scaleF = 1.8 - p * 0.8;
        alpha = p;
        offsetY = 0;
      } else if (t < 0.65) {
        scaleF = 1;
        alpha = 1;
        offsetY = 0;
      } else {
        const p = (t - 0.65) / 0.35;
        scaleF = 1;
        alpha = 1 - p;
        offsetY = -p * 25;
      }

      const cx = Math.trunc(Game.WIDTH / 2);
      const cy = Math.trunc(Game.HEIGHT / 2) - 20 + Math.trunc(offsetY);
      const a = Math.trunc(alpha * 255);

      const fontSize = Math.trunc(64 * scaleF);
      if (fontSize > 8) {
        g2.setFont(HUD.FONT_ANNOUNCE.deriveFont(fontSize));
        g2.setColor(new Color(255, 210, 80, Math.min(a, 255)));
        fm = g2.getFontMetrics();
        g2.drawString(this.announceText, cx - Math.trunc(fm.stringWidth(this.announceText) / 2), cy);
      }

      if (this.announceSubtext.length > 0 && t > 0.08) {
        const subAlpha = Math.min((t - 0.08) / 0.12, 1) * alpha;
        g2.setFont(HUD.FONT_ANNOUNCE_SUB);
        g2.setColor(new Color(200, 200, 200, Math.trunc(subAlpha * 180)));
        fm = g2.getFontMetrics();
        g2.drawString(this.announceSubtext, cx - Math.trunc(fm.stringWidth(this.announceSubtext) / 2), cy + 35 + Math.trunc(offsetY));
      }

      if (t > 0.08 && t < 0.8) {
        let lineP = Math.min((t - 0.08) / 0.15, 1);
        if (t > 0.65) lineP *= alpha;
        const lineW = Math.trunc(200 * lineP);
        g2.setColor(new Color(255, 210, 80, Math.trunc(lineP * 100)));
        g2.fillRect(cx - Math.trunc(lineW / 2), cy + 8 + Math.trunc(offsetY), lineW, 2);
      }
    }

    // Level progress bar — bottom of screen
    if (!Game.isBossActive()) {
      const progress = Game.getLevelProgress();
      const progH = 4;
      const progY = Game.HEIGHT - progH;

      // Background
      g2.setColor(HUD.BAR_BG);
      g2.fillRect(0, progY, Game.WIDTH, progH);

      // Fill
      const progFillW = Math.trunc(Game.WIDTH * progress);
      if (progFillW > 0) {
        // Flash brighter near completion
        const barColor = progress > 0.85
          ? this.lerpColor(GamePalette.accent(), HUD.GOLD, (progress - 0.85) / 0.15)
          : GamePalette.accent();
        g2.setColor(barColor);
        g2.fillRect(0, progY, progFillW, progH);
      }

      // Completion flash — the level-up banner flash handles the burst
      if (this.levelUpBanner > 0.5) {
        g2.setColor(new Color(HUD.GOLD.getRed(), HUD.GOLD.getGreen(), HUD.GOLD.getBlue(),
          Math.trunc(this.levelUpBanner * 200)));
        g2.fillRect(0, progY, Game.WIDTH, progH);
      }
    }

    // Achievement toast
    if (this.currentToast !== null && this.toastSlide > 0.01) {
      this.renderToast(g2);
    }
  }

  private renderToast(g: Graphics2D): void {
    const toastW = 280;
    const toastH = 48;
    const toastX = Game.WIDTH - toastW - 20;
    const baseY = 80;
    const toastY = baseY - Math.trunc((1 - this.toastSlide) * 60); // slides down from above
    const alpha = Math.trunc(this.toastSlide * 255);

    // Background
    g.setColor(new Color(22, 30, 44, Math.trunc(alpha * 0.9)));
    g.fillRoundRect(toastX, toastY, toastW, toastH, 8, 8);
    // Accent left strip
    g.setColor(new Color(255, 210, 80, alpha));
    g.fillRoundRect(toastX, toastY, 4, toastH, 4, 4);
    // Border
    g.setColor(new Color(255, 210, 80, Math.trunc(alpha / 3)));
    g.drawRoundRect(toastX, toastY, toastW, toastH, 8, 8);

    // Trophy icon
    g.setFont(HUD.FONT_TOAST_ICON);
    g.setColor(new Color(255, 210, 80, alpha));
    g.drawString('★', toastX + 14, toastY + 31);

    // Label
    g.setFont(HUD.FONT_TIER);
    g.setColor(new Color(255, 210, 80, alpha));
    g.drawString('ACHIEVEMENT UNLOCKED', toastX + 38, toastY + 18);

    // Name
    g.setFont(HUD.FONT_STAT);
    g.setColor(new Color(230, 234, 240, alpha));
    g.drawString(this.currentToast!, toastX + 38, toastY + 37);
  }

  private drawTierBar(g: Graphics2D, x: number, y: number, label: string, tier: number, color: Color): void {
    g.setFont(HUD.FONT_TIER);
    g.setColor(HUD.TEXT_DIM);
    g.drawString(label, x, y + 9);

    const dotX = x + 30;
    const dotSize = 8;
    const dotGap = 4;
    for (let i = 0; i < HUD.MAX_TIER; i++) {
      if (i < tier) {
        g.setColor(color);
        g.fillRoundRect(dotX + i * (dotSize + dotGap), y + 1, dotSize, dotSize, 3, 3);
      } else {
        g.setColor(HUD.TIER_BG);
        g.fillRoundRect(dotX + i * (dotSize + dotGap), y + 1, dotSize, dotSize, 3, 3);
      }
    }

    if (tier >= HUD.MAX_TIER) {
      g.setFont(HUD.FONT_TIER);
      g.setColor(color);
      g.drawString('MAX', dotX + HUD.MAX_TIER * (dotSize + dotGap) + 4, y + 9);
    }
  }

  private getHealthColor(pct: number): Color {
    if (pct > 0.5) {
      const t = (pct - 0.5) * 2;
      return this.lerpColor(HUD.HEALTH_MID, HUD.HEALTH_HIGH, t);
    } else {
      const t = pct * 2;
      return this.lerpColor(HUD.HEALTH_LOW, HUD.HEALTH_MID, t);
    }
  }

  private lerpColor(a: Color, b: Color, t: number): Color {
    t = Math.max(0, Math.min(1, t));
    return new Color(
      Math.trunc(a.getRed() + (b.getRed() - a.getRed()) * t),
      Math.trunc(a.getGreen() + (b.getGreen() - a.getGreen()) * t),
      Math.trunc(a.getBlue() + (b.getBlue() - a.getBlue()) * t));
  }

  private getPlayerMultiplier(): number {
    // Find player in handler to read multiplier
    // This is called from tick which is in the game loop, so handler is accessible via Game
    // For simplicity, we'll make this work through a static reference
    return Game.getPlayerMultiplier();
  }

  getWaveCount(): number { return this.waveCount; }

  setScore(score: number): void { this.score = score; }
  getScore(): number { return this.score; }
  getLevel(): number { return this.level; }
  setLevel(level: number): void { this.level = level; }
  getPoints(): number { return this.points; }
  setPoints(points: number): void { this.points = points; }
  addKillBonus(bonus: number): void { this.score += bonus; this.points += bonus; }

  // Stats tracking
  getTicksSurvived(): number { return this.ticksSurvived; }
  getHealthUpgrades(): number { return this.healthUpgrades; }
  getSpeedUpgrades(): number { return this.speedUpgrades; }
  getRefills(): number { return this.refills; }
  addHealthUpgrade(): void { this.healthUpgrades++; }
  addSpeedUpgrade(): void { this.speedUpgrades++; }
  addRefill(): void { this.refills++; }
  getTotalUpgrades(): number { return this.healthUpgrades + this.speedUpgrades + this.refills; }

  getTimeSurvived(): string {
    const totalSec = Math.trunc(this.ticksSurvived / 60);
    const min = Math.trunc(totalSec / 60);
    const sec = totalSec % 60;
    if (min > 0) return min + 'm ' + sec + 's';
    return sec + 's';
  }

  resetStats(): void {
    this.ticksSurvived = 0;
    this.healthUpgrades = 0;
    this.speedUpgrades = 0;
    this.refills = 0;
    this.kills = 0;
    this.levelUpBanner = 0;
    this.scorePulse = 0;
    this.lastMilestone = 0;
    this.announceTimer = 0;
    this.waveCount = 0;
    this.lastBossLevel = 0;
    this.isWaveAnnounce = false;
  }

  getKills(): number { return this.kills; }
  addKill(): void { this.kills++; }

  private static readonly ABILITY_READY = new Color(78, 205, 196);
  private static readonly ABILITY_COOLDOWN = new Color(50, 60, 75);
  private static readonly SHIELD_COLOR = new Color(140, 220, 255);
  private static readonly SLOWMO_COLOR = new Color(180, 140, 255);
  private static readonly SLOWMO_ACTIVE = new Color(220, 180, 255);
  private static readonly ABILITY_BORDER_OFF = new Color(60, 60, 80);
  private static readonly ABILITY_ICON_OFF = new Color(80, 80, 100);
  private static readonly AMMO_COLOR = new Color(255, 210, 80);
  private static readonly PARRY_COLOR = new Color(78, 205, 196);
  private static readonly FONT_ABILITY = new Font('Arial', Font.BOLD, 10);
  private static readonly FONT_CHARGE = new Font('Arial', Font.BOLD, 12);

  private renderAbilityBar(g: Graphics2D, x: number, y: number): void {
    // Find player
    let player: Player | null = null;
    const handler = Game.getHandler();
    if (handler === null) return;
    const objects: GameObject[] = handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      if (obj instanceof Player) { player = obj; break; }
    }
    if (player === null) return;

    const iconSize = 22;
    const gap = 8;
    let ix = x;

    // === DASH ===
    const dashPct = player.getDashCooldownPct();
    const dashReady = dashPct <= 0;
    this.drawAbilityIcon(g, ix, y, iconSize, 'SHIFT', dashReady, dashPct, HUD.ABILITY_READY);
    ix += iconSize + gap;

    // === SHIELD ===
    const shieldUp = player.isShieldActive();
    const shieldPct = player.getShieldCooldownPct();
    this.drawAbilityIcon(g, ix, y, iconSize, 'SHD', shieldUp, shieldUp ? 0 : shieldPct, HUD.SHIELD_COLOR);
    ix += iconSize + gap;

    // === SLOW-MO ===
    const charges = player.getSlowmoCharges();
    const slowActive = player.isSlowmoActive();
    const slowPct = player.getSlowmoTimerPct();
    const regenPct = player.getSlowmoRegenPct();
    const recharging = !slowActive && charges < player.getSlowmoMaxCharges();
    const slowCol = slowActive ? HUD.SLOWMO_ACTIVE : HUD.SLOWMO_COLOR;

    // Background
    g.setColor(HUD.ABILITY_COOLDOWN);
    g.fillRoundRect(ix, y, iconSize, iconSize, 4, 4);

    if (slowActive) {
      // Active timer fill (draining)
      const fillH = Math.trunc(iconSize * slowPct);
      g.setColor(new Color(slowCol.getRed(), slowCol.getGreen(), slowCol.getBlue(), 100));
      g.fillRoundRect(ix, y + iconSize - fillH, iconSize, fillH, 4, 4);
    } else if (recharging) {
      // Regen progress fill (building up)
      const fillH = Math.trunc(iconSize * regenPct);
      g.setColor(new Color(slowCol.getRed(), slowCol.getGreen(), slowCol.getBlue(), 40));
      g.fillRoundRect(ix, y + iconSize - fillH, iconSize, fillH, 4, 4);
    }

    // Border
    g.setColor(charges > 0 || slowActive ? slowCol : HUD.ABILITY_BORDER_OFF);
    g.drawRoundRect(ix, y, iconSize, iconSize, 4, 4);

    // Hourglass icon
    const iconColor = charges > 0 || slowActive ? slowCol : HUD.ABILITY_ICON_OFF;
    g.setColor(iconColor);
    const cx = ix + Math.trunc(iconSize / 2);
    const cy = y + Math.trunc(iconSize / 2);
    g.drawLine(cx - 5, cy - 6, cx + 5, cy - 6);
    g.drawLine(cx - 5, cy - 6, cx, cy);
    g.drawLine(cx + 5, cy - 6, cx, cy);
    g.drawLine(cx - 5, cy + 6, cx + 5, cy + 6);
    g.drawLine(cx - 5, cy + 6, cx, cy);
    g.drawLine(cx + 5, cy + 6, cx, cy);

    // === COMBAT MODE: Ammo + Parry indicators ===
    if (Game.combatMode) {
      ix += iconSize + gap;

      // AMMO — draw as small dots
      const ammo = player.getAmmo();
      const maxAmmo = player.getMaxAmmo();
      g.setColor(HUD.ABILITY_COOLDOWN);
      g.fillRoundRect(ix, y, iconSize, iconSize, 4, 4);
      g.setColor(ammo > 0 ? HUD.AMMO_COLOR : HUD.ABILITY_BORDER_OFF);
      g.drawRoundRect(ix, y, iconSize, iconSize, 4, 4);
      // Ammo dots
      const dotSize = 3;
      const dotGap = 2;
      const totalDotW = maxAmmo * dotSize + (maxAmmo - 1) * dotGap;
      const dotX = ix + Math.trunc((iconSize - totalDotW) / 2);
      const dotY = y + Math.trunc(iconSize / 2) - Math.trunc(dotSize / 2);
      for (let i = 0; i < maxAmmo; i++) {
        g.setColor(i < ammo ? HUD.AMMO_COLOR : new Color(60, 60, 80));
        g.fillOval(dotX + i * (dotSize + dotGap), dotY, dotSize, dotSize);
      }

      ix += iconSize + gap;

      // PARRY
      const parryPct = player.getParryCooldownPct();
      const parryReady = parryPct <= 0;
      const parryActive = player.isParrying();
      const parryCol = parryActive ? new Color(120, 240, 230) : HUD.PARRY_COLOR;
      this.drawAbilityIcon(g, ix, y, iconSize, 'PRY', parryReady, parryPct, parryCol);
    }
  }

  private drawAbilityIcon(g: Graphics2D, x: number, y: number, size: number, label: string,
                          ready: boolean, cooldownPct: number, readyColor: Color): void {
    // Background
    g.setColor(HUD.ABILITY_COOLDOWN);
    g.fillRoundRect(x, y, size, size, 4, 4);

    // Cooldown sweep (fill from bottom up)
    if (!ready && cooldownPct > 0) {
      const fillH = Math.trunc(size * (1 - cooldownPct));
      g.setColor(new Color(readyColor.getRed(), readyColor.getGreen(), readyColor.getBlue(), 40));
      g.fillRoundRect(x, y + size - fillH, size, fillH, 4, 4);
    }

    // Border
    g.setColor(ready ? readyColor : HUD.ABILITY_BORDER_OFF);
    g.drawRoundRect(x, y, size, size, 4, 4);

    // Icon
    const iconColor = ready ? readyColor : HUD.ABILITY_ICON_OFF;
    g.setColor(iconColor);
    const cx = x + Math.trunc(size / 2);
    const cy = y + Math.trunc(size / 2);

    if ('SHD' === label) {
      // Shield shape
      const sx = [cx - 5, cx + 5, cx + 5, cx, cx - 5];
      const sy = [cy - 5, cy - 5, cy + 1, cy + 6, cy + 1];
      g.drawPolygon(sx, sy, 5);
    } else if ('SHIFT' === label) {
      // Double chevron >> (speed)
      const oldStroke: BasicStroke = g.getStroke();
      g.setStroke(new BasicStroke(1.5, BasicStroke.CAP_ROUND, BasicStroke.JOIN_ROUND));
      g.drawLine(cx - 4, cy - 4, cx - 1, cy);
      g.drawLine(cx - 1, cy, cx - 4, cy + 4);
      g.drawLine(cx, cy - 4, cx + 3, cy);
      g.drawLine(cx + 3, cy, cx, cy + 4);
      g.setStroke(oldStroke);
    } else {
      // Fallback: text
      g.setFont(HUD.FONT_ABILITY);
      const fm: FontMetrics = g.getFontMetrics();
      g.drawString(label, x + Math.trunc((size - fm.stringWidth(label)) / 2), y + Math.trunc(size / 2) + 4);
    }
  }
}
